import React from 'react';
import styled from 'styled-components';
import { APP_CONFIG, LAST_UPDATED_PRIVACY } from '../config/constants';

const PrivacySection = styled.section`
  background: transparent;
  min-height: calc(100vh - 80px);
  padding: 4rem 0;
`;

const PrivacyTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  color: rgba(255, 255, 255, 0.95);
  text-align: center;
  font-weight: 700;
`;

const LastUpdated = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
  margin-bottom: 3rem;
  font-style: italic;
`;

const PrivacyContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 3rem;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);

  h2 {
    color: rgba(255, 255, 255, 0.95);
    font-size: 1.5rem;
    margin-top: 2.5rem;
    margin-bottom: 1rem;
    font-weight: 600;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);

    &:first-child {
      margin-top: 0;
    }
  }

  h3 {
    color: rgba(255, 255, 255, 0.95);
    font-size: 1.1rem;
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
    font-weight: 600;
  }

  p {
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.8;
    margin-bottom: 1rem;
  }

  ul {
    margin: 1rem 0;
    padding-left: 1.5rem;
  }

  li {
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.8;
    margin-bottom: 0.5rem;
  }

  strong {
    color: rgba(255, 255, 255, 0.95);
    font-weight: 600;
  }

  a {
    color: rgba(255, 255, 255, 0.95);
    text-decoration: none;
    font-weight: 500;
    border-bottom: 1px solid transparent;
    transition: all 0.3s;

    &:hover {
      text-decoration: underline;
      border-bottom-color: rgba(255, 255, 255, 0.95);
    }
  }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const Privacy = () => {
  return (
    <PrivacySection>
      <div className="container">
        <PrivacyTitle>Privacy Policy</PrivacyTitle>
        <LastUpdated>Last Updated: {LAST_UPDATED_PRIVACY}</LastUpdated>
        <PrivacyContent>
          <h2>1. Introduction</h2>
          <p>
            Welcome to {APP_CONFIG.name} (the "App"). {APP_CONFIG.company} ("we," "us," "our," or "Company") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application.
          </p>
          <p>
            Please read this Privacy Policy carefully. If you do not agree with our policies and practices, please do not use our App. By accessing and using {APP_CONFIG.name}, you acknowledge that you have read, understood, and agree to be bound by all the terms of this Privacy Policy.
          </p>
          <p>
            <strong>Age Requirement:</strong> {APP_CONFIG.name} is intended for users aged 13 and older. We do not knowingly collect or solicit personal information from children under 13. If we become aware that we have collected information from a child under 13 without parental consent, we will delete such information promptly.
          </p>

          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Information You Provide Directly</h3>
          <p><strong>Account Registration:</strong></p>
          <ul>
            <li>Email address</li>
            <li>First and last name</li>
            <li>Password</li>
            <li>Timezone</li>
            <li>Profile picture if you provide one</li>
            <li>If you use Google or Apple Sign-In, we collect your email address and id from the provider for authentication purposes only. We do not store or process any other profile data from these services.</li>
          </ul>

          <p><strong>Subscription Information:</strong></p>
          <ul>
            <li>Apple StoreKit subscription data (product ID, transaction ID, subscription status, renewal dates)</li>
            <li>This information is provided by Apple and stored securely in our database</li>
          </ul>

          <p><strong>Profile Information:</strong></p>
          <ul>
            <li>Account settings and preferences</li>
          </ul>

          <h3>2.2 Information Collected Automatically</h3>
          <p><strong>Check-in Data:</strong></p>
          <ul>
            <li>Check-in events include your user ID, timestamp, timezone.</li>
            <li><strong>Important:</strong> The App does not have access to or collect information about which specific activity you chose as your trigger—only that a trigger event occurred. You configure your trigger using the iOS Shortcuts app, and the App only registers that a check-in event was triggered.</li>
          </ul>

          <p><strong>Device Information:</strong></p>
          <ul>
            <li>Device identifiers for push notifications</li>
            <li>Device platform (iOS)</li>
            <li>IP address (for logging and security purposes)</li>
          </ul>

          <p><strong>Log Data:</strong></p>
          <ul>
            <li>We collect logs for monitoring and troubleshooting purposes, which may include your user ID and IP address</li>
            <li>Sensitive information is redacted from logs</li>
            <li>Log data is stored in Google Cloud Platform</li>
          </ul>

          <p><strong>Usage Information:</strong></p>
          <ul>
            <li>How you interact with the App, such as actions taken and features used</li>
          </ul>

          <h3>2.3 Information from Groups</h3>
          <p>When you create a create and invite others, we collect:</p>
          <ul>
            <li>Group name and description</li>
            <li>Picture if provided</li>
            <li>Emails for group member invitations</li>
          </ul>

          <p>When you connect with other users or join groups, we collect:</p>
          <ul>
            <li>Information about your group memberships</li>
            <li>Whether you choose to share your check-in history and future check-ins with specific users or groups</li>
          </ul>

          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, maintain, and improve the App</li>
            <li>Create and manage your account and authentication</li>
            <li>Register check-in events and send notifications to you and people in your groups</li>
            <li>Process subscriptions and billing</li>
            <li>Communicate with you regarding your account and service updates</li>
            <li>Detect and prevent fraud and security issues</li>
            <li>Comply with legal obligations</li>
          </ul>

          <h2>4. How We Share Your Information</h2>
          
          <h3>4.1 Information Shared with Your Consent</h3>
          <ul>
            <li><strong>Check-in Data:</strong> If you choose to share your check-ins with a group, your check-in history and future check-in events will be visible to users in the group. You can stop sharing or leave a group at any time.</li>
          </ul>

          <h3>4.2 Third-Party Service Providers</h3>
          <p>
            We may share your information with third-party vendors, service providers, contractors, or agents who perform services for us or on our behalf and require access to such information to carry out their work. These third parties help us operate, maintain, and improve the App.
          </p>
          <p>Examples of such services include:</p>
          <ul>
            <li>Push Notification Services: Delivering push notifications and alerts to your device.</li>
            <li>Email Service Providers: Sending transactional emails such as password resets and account verification messages.</li>
            <li>Cloud Service Providers: Hosting and managing user data and application infrastructure.</li>
          </ul>

          <h3>4.3 Legal Requirements</h3>
          <p>We may disclose your information if required to do so by law or if we have a good-faith belief that such disclosure is necessary to:</p>
          <ul>
            <li>Comply with legal obligations, court orders, or government requests</li>
            <li>Enforce our Terms of Service and other agreements</li>
            <li>Protect the safety, rights, and property of our users and the public</li>
          </ul>

          <h3>4.4 No Sale of Data</h3>
          <p>
            We do not collect information for advertising purposes. We do not sell, trade, or rent your personal information to third parties for any purpose.
          </p>

          <h2>5. Data Retention</h2>
          <ul>
            <li><strong>Check-in Data:</strong> Check-in records are retained indefinitely unless you delete your account.</li>
            <li><strong>Account Data:</strong> If you delete your account, all personal data associated with your account—including check-in history, connections, group memberships, subscription records, and pending invitations—are immediately and permanently deleted from our database.</li>
            <li><strong>Log Data:</strong> Logs may contain user IDs but are retained separately from your account data. When logs are purged, any references to your user ID are removed.</li>
          </ul>

          <h2>6. Your Privacy Rights and Choices</h2>
          
          <h3>6.1 Access and Control</h3>
          <p>You can access, update, or modify your account information at any time through the App's settings.</p>

          <h3>6.2 Data Deletion</h3>
          <p>You have the right to delete your account and all associated data. You can do this through the App's settings tab. Upon deletion, all your personal data will be immediately and permanently removed from our systems.</p>

          <h3>6.3 Sharing Preferences</h3>
          <p>You can control who sees your check-in data by:</p>
          <ul>
            <li>Choosing whether to share check-ins when accepting group or connection invitations</li>
            <li>Stopping sharing with specific groups or connections at any time</li>
            <li>Leaving groups without deleting your account</li>
          </ul>

          <h3>6.4 Activity Trigger Management</h3>
          <p>You can change your activity trigger, pause your automation, or stop registering check-ins at any time through the iOS Shortcuts app configuration.</p>

          <h3>6.5 Notification Preferences</h3>
          <p>You can enable or disable notifications through your device's settings and through the App's settings.</p>

          <h3>6.6 California Consumer Privacy Act (CCPA)</h3>
          <p>If you are a California resident, you have the right to:</p>
          <ul>
            <li>Know what personal information is collected, used, shared, and sold</li>
            <li>Delete personal information collected (subject to certain exceptions)</li>
            <li>Opt-out of the sale of personal information (note: we do not sell personal information)</li>
            <li>Non-discrimination for exercising your CCPA rights</li>
          </ul>
          <p>To exercise these rights, please contact us at {APP_CONFIG.email}.</p>

          <h2>7. Data Security</h2>
          <p>We implement industry-standard security measures to protect your information, including:</p>
          <ul>
            <li>Encryption in transit and at rest</li>
            <li>Secure password storage</li>
            <li>Restricted access to user data</li>
          </ul>
          <p>However, no security method is completely secure. While we work to protect your information, we cannot guarantee absolute security.</p>

          <h2>8. Third-Party Links and Services</h2>
          <p>
            The App may contain links to third-party websites or services that are not operated by us. This Privacy Policy does not apply to third-party services, and we are not responsible for their privacy practices. We encourage you to review their privacy policies before providing your information.
          </p>

          <h2>9. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices, technology, or legal requirements. We will notify you of material changes by updating the "Last Updated" date at the top of this policy and, if required, by sending you an email or displaying a notification in the App.
          </p>
          <p>Your continued use of the App following the posting of revised Privacy Policy means you accept and agree to the changes.</p>

          <h2>10. Contact Us</h2>
          <p>If you have questions about this Privacy Policy, your privacy, or our privacy practices, please contact us at:</p>
          <p>Email: <a href={`mailto:${APP_CONFIG.email}`}>{APP_CONFIG.email}</a></p>

        </PrivacyContent>
      </div>
    </PrivacySection>
  );
};

export default Privacy;
